import { createContext, useState, useContext } from "react";
import { useError } from "../ErrorContext"
import { getRunMessagesAPI,createRunMessageAPI } from "../../services/runsAPI"
import { useUser } from "@contexts/UserContext";
import { unpauseNotificationsAPI, pauseNotificationsAPI } from "@services/liveTrakAPI";
import { useSuccess } from "@contexts/SuccessContext";

const sortMessagesByDate = (messages) => {
    return messages.sort((a,b) => {
        return new Date(b.CreatedAt) - new Date(a.CreatedAt)
    })
}

const RunMessagesContext = createContext();

/**
 * Context to wrap around RunMessage and related components. 
 * Dialog boxes for Pausing/Unpausing notifications require access to message state when sending new messages, in order to add the message to Messages list right away for sender. 
 * This way, they don't have to wait 10s for an update to come in, and we can avoid prop-drilling of 'messages' state.
 * 
 * @param {Function} setNotificationsPaused - setter from useLiveTrackData. Passed in here to speed up notificationsPaused state update. This way, RunMessages doesn't have to wait 10 seconds for the update before showing the message sender that the notifications were successfully paused or unpaused.
 * 
 * @returns {RunMessage[]} messages
 * @returns {Function} fetchMessages - get all messages for a run
 * @returns {string | null} submitting - feedback to tell user if messages are currently being sent
 * @returns {Function} setSubmitting
 * @returns {Function} handleCreateRunMessage
 * @returns {Function} handlePauseUnpause 
 */
export const RunMessagesProvider = ({ children, setNotificationsPaused }) => {
    const [messages, setMessages] = useState([]);
    const [submitting, setSubmitting] = useState(null); //to display user feedback in RunMessages

    const {showError} = useError();
    const { user, userGroups } = useUser();
    const { timeoutSuccess } = useSuccess();
    
    const hasPermission = userGroups?.includes("Admin") || userGroups?.includes("Project Manager") || userGroups?.includes("Supervisor")

    const addMessage = (newMessage) => {
        const messagesSortedByDate = sortMessagesByDate([...messages, newMessage])

        setMessages(messagesSortedByDate);
    };

    //submission for creating run messages 
    const handleCreateRunMessage = async(message, selectedRun) => {
        setSubmitting("Sending message...")
    
        if(!message){
            showError("Please include a message")
            return
        }
    
        if(!hasPermission){
            showError('You do not have permission to send messages')
            return
        }
    
        const response = await createRunMessageAPI(selectedRun, {
            Message: message,
            CreatedBy: user.UserID
        })
    
        if(!response.success){
            showError(response.message)
            setSubmitting(null)
            return
        }
    
        //add to message state in RunMessageContext
        addMessage({
            Message: message,
            CreatedAt: response.data.CreatedAt,
            CreatedBy: {
                    serID: user.UserID,
                First_Name: user.First_Name,
                Last_Name: user.Last_Name
            }
        })
            
        timeoutSuccess("Message sent successfully", 4000) //can consider changing message depending on whether they chose to send to devices
        setSubmitting(null)
    }

    //submission for pausing and unpausing notifications
    const handlePauseUnpause = async(message, selectedRun, isPaused, setShowModal) => {
        setSubmitting("Sending message...")

        if(!message){
            showError("Please include a message")
            setSubmitting(null)
            return
        }

        if(!hasPermission){
            showError(`You do not have permission to ${isPaused ? 'unpause' : 'pause'} notifications`)
            setSubmitting(null)
            return
        }

        setShowModal(false)

        //create message
        const response = await createRunMessageAPI(selectedRun, {
            Message: message,
            CreatedBy: user.UserID
        })

        if(!response.success){
            showError(response.message)
            setSubmitting(null)
            return
        }

        //add to message state in RunMessageContext
        addMessage({
            Message: message,
            CreatedAt: response.data.CreatedAt,
            CreatedBy: {
                UserID: user.UserID,
                First_Name: user.First_Name,
                Last_Name: user.Last_Name
            }
        })

        if(setNotificationsPaused){
            setNotificationsPaused(!isPaused)
        }

        const apiCall = isPaused ? unpauseNotificationsAPI : pauseNotificationsAPI

        //pause or unpause notifications, depending on current state
        const notificationResponse = await apiCall(selectedRun)

        if(!notificationResponse.success){
            showError(response.message)
            return
        }
        

        const successMessage = `Message sent and notifications ${isPaused ? "unpaused" : "paused"} successfully`
        timeoutSuccess(successMessage, 3000) //can consider changing message depending on whether they chose to send to devices
        setSubmitting(null)
    }

    const fetchMessages = async(selectedRun) => {
        if(!selectedRun){
            setMessages([])
            return
        } 

        //call api
        const response = await getRunMessagesAPI(selectedRun)
          
        //handle error
        if(!response.success){
            showError(response.message);
            return
        }
        //set state with messages, sort by date/time
        const messagesSortedByDate = sortMessagesByDate(response.data)

        setMessages(messagesSortedByDate)
    }

    return (
        <RunMessagesContext.Provider 
            value={{ 
                messages, 
                fetchMessages, 
                submitting, 
                setSubmitting,
                handleCreateRunMessage,
                handlePauseUnpause,
                setNotificationsPaused 
            }}
        >
            {children}
        </RunMessagesContext.Provider>
    );
};

export const useRunMessages = () => useContext(RunMessagesContext);
